/* eslint-disable */

import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash/debounce';
import Autosuggest from 'react-autosuggest';
import Chip from '@material-ui/core/Chip';
import Paper from '@material-ui/core/Paper';
import Popper from '@material-ui/core/Popper';
import MenuItem from '@material-ui/core/MenuItem';
import { withStyles } from '@material-ui/core/styles';
import parse from 'autosuggest-highlight/parse';
import match from 'autosuggest-highlight/match';
import blue from '@material-ui/core/colors/blue';
import compose from 'recompose/compose';
import classNames from 'classnames';
import { list } from '../lib/api';
import AutocompleteArrayInputChip from './AutocompleteArrayInputChip';

import { addField, translate, FieldTitle } from 'react-admin';

const styles = theme => ({
  container: {
    flexGrow: 1,
    position: 'relative',
  },
  root: {},
  suggestionsContainerOpen: {
    position: 'absolute',
    marginBottom: theme.spacing.unit * 3,
    zIndex: 2,
  },
  suggestion: {
    display: 'block',
    fontFamily: theme.typography.fontFamily,
  },
  suggestionText: { fontWeight: 300 },
  highlightedSuggestionText: { fontWeight: 500 },
  suggestionsList: {
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
  chip: {
    marginRight: theme.spacing.unit,
  },
  chipDisabled: {
    pointerEvents: 'none',
  },
  chipFocused: {
    backgroundColor: blue[300],
  },
});

export class AutocompleteArrayInput extends React.Component {
  inputEl = null;

  lastRequestToken = null;

  state = {
    dirty: false,
    inputValue: null,
    searchText: '',
    suggestions: [],
    isLoading: false,
    selectedSuggestions: [],
  };

  componentWillMount() {
    const { input } = this.props;
    this.setState({
      inputValue: input.value,
    });
  }

  componentWillReceiveProps(nextProps) {
    const { input } = nextProps;
    if (!isEqual(input.value, this.state.inputValue)) {
      this.setState({
        inputValue: input.value,
        dirty: false,
      });
      // Ensure to reset the filter
      this.updateFilter('');
    }
  }

  getSuggestionValue = suggestion => get(suggestion, this.props.optionValue);

  getSuggestionText = suggestion => {
    if (!suggestion) return '';

    return suggestion.name;
  };

  handleSuggestionSelected = (event, { suggestion, method }) => {
    const { input } = this.props;

    this.setState(
      ({ selectedSuggestions }) => ({
        selectedSuggestions: [...selectedSuggestions, suggestion],
      }),
      () => {
        input.onChange([...this.state.inputValue, this.getSuggestionValue(suggestion)]);
      },
    );

    if (method === 'enter') {
      event.preventDefault();
    }
  };

  loadSuggestions = debounce(value => {
    if (!value) return;

    list('users/base', { full_name: value })
      .then(res => {
        this.setState({
          suggestions: res.data,
        });
      })
      .catch(() =>
        this.setState({
          suggestions: [],
        }),
      );
  }, 500);

  handleSuggestionsFetchRequested = () => {
    this.loadSuggestions(this.state.searchText);
  };

  handleSuggestionsClearRequested = () => {
    this.updateFilter('');
  };

  handleMatchSuggestionOrFilter = inputValue => {
    this.setState({
      dirty: true,
      searchText: inputValue,
    });
    this.updateFilter(inputValue);
    this.loadSuggestions(inputValue);
  };

  handleChange = (event, { newValue, method }) => {
    switch (method) {
      case 'type':
      case 'escape':
        this.handleMatchSuggestionOrFilter(newValue);
        break;
    }
  };

  renderInput = inputProps => {
    const { input } = this.props;
    const {
      autoFocus,
      className,
      classes,
      isRequired,
      label,
      meta,
      onChange,
      resource,
      source,
      value,
      ref,
      options: { InputProps, ...options },
      ...other
    } = inputProps;
    if (typeof meta === 'undefined') {
      throw new Error(
        "The TextInput component wasn't called within a redux-form <Field>. Did you decorate it and forget to add the addField prop to your component? See https://marmelab.com/react-admin/Inputs.html#writing-your-own-input-component for details.",
      );
    }

    const { touched, error, helperText = false } = meta;

    // We need to store the input reference for our Popper element containg the suggestions
    // but Autosuggest also needs this reference (it provides the ref prop)
    const storeInputRef = input => {
      this.inputEl = input;
      ref(input);
    };

    return (
      <AutocompleteArrayInputChip
        clearInputValueOnChange
        onUpdateInput={onChange}
        onAdd={this.handleAdd}
        onDelete={this.handleDelete}
        value={input.value}
        inputRef={storeInputRef}
        error={touched && error}
        helperText={touched && error && helperText}
        chipRenderer={this.renderChip}
        label={
          <FieldTitle label={label} source={source} resource={resource} isRequired={isRequired} />
        }
        {...other}
        {...options}
      />
    );
  };

  renderChip = ({ value, isFocused, isDisabled, handleClick, handleDelete }, key) => {
    const { classes = {} } = this.props;
    const label = this.state.selectedSuggestions.filter(s => s.id === value)[0];
    if (!label) return null;

    return (
      <Chip
        key={key}
        className={classNames(classes.chip, {
          [classes.chipDisabled]: isDisabled,
          [classes.chipFocused]: isFocused,
        })}
        onClick={handleClick}
        onDelete={handleDelete}
        label={label.name}
      />
    );
  };

  handleAdd = chip => {
    const { input, limitChoicesToValue } = this.props;

    const filteredChoices = this.state.suggestions.filter(suggestion => {
      const val = this.getSuggestionValue(suggestion);
      return this.state.inputValue.includes(val);
    });

    const choice =
      filteredChoices.length === 1
        ? filteredChoices[0]
        : filteredChoices.find(c => this.getSuggestionValue(c) === chip);

    if (choice) {
      this.setState(oldState => ({
        selectedSuggestions: [...oldState.selectedSuggestions, choice],
      }));
      return input.onChange([...this.state.inputValue, this.getSuggestionValue(choice)]);
    }

    if (limitChoicesToValue) {
      // Ensure to reset the filter
      this.updateFilter('');
      return;
    }

    input.onChange([...this.state.inputValue, chip]);
  };

  handleDelete = chip => {
    const { input } = this.props;
    this.setState(oldState => ({
      selectedSuggestions: oldState.selectedSuggestions.filter(s => s.id !== chip),
    }));

    input.onChange(this.state.inputValue.filter(value => value !== chip));
  };

  renderSuggestionsContainer = options => {
    const {
      containerProps: { className, ...containerProps },
      children,
    } = options;

    return (
      <Popper className={className} open anchorEl={this.inputEl} placement="bottom-start">
        <Paper square {...containerProps}>
          {children}
        </Paper>
      </Popper>
    );
  };

  renderSuggestionComponent = ({ suggestion, query, isHighlighted, ...props }) => (
    <div {...props} />
  );

  renderSuggestion = (suggestion, { query, isHighlighted }) => {
    const label = this.getSuggestionText(suggestion);
    const matches = match(label, query);
    const parts = parse(label, matches);
    const { classes = {}, suggestionComponent } = this.props;

    return (
      <MenuItem
        selected={isHighlighted}
        component={suggestionComponent || this.renderSuggestionComponent}
        suggestion={suggestion}
        query={query}
        isHighlighted={isHighlighted}
      >
        <div>
          {parts.map((part, index) => {
            return part.highlight ? (
              <span key={index} className={classes.highlightedSuggestionText}>
                {part.text}
              </span>
            ) : (
              <strong key={index} className={classes.suggestionText}>
                {part.text}
              </strong>
            );
          })}
        </div>
      </MenuItem>
    );
  };

  handleFocus = () => {
    const { input } = this.props;
    input && input.onFocus && input.onFocus();
  };

  updateFilter = value => {
    if (this.previousFilterValue !== value) {
      this.setState({
        searchText: value,
      });
      if (value === '') {
        this.setState({
          suggestions: [],
        });
      }
    }
    this.previousFilterValue = value;
  };

  shouldRenderSuggestions = () => true;

  render() {
    const {
      alwaysRenderSuggestions,
      classes = {},
      isRequired,
      label,
      meta,
      resource,
      source,
      className,
      options,
    } = this.props;
    const { suggestions, searchText } = this.state;

    return (
      <Autosuggest
        theme={{
          container: classes.container,
          suggestionsContainerOpen: classes.suggestionsContainerOpen,
          suggestionsList: classes.suggestionsList,
          suggestion: classes.suggestion,
        }}
        renderInputComponent={this.renderInput}
        suggestions={suggestions}
        alwaysRenderSuggestions={alwaysRenderSuggestions}
        onSuggestionSelected={this.handleSuggestionSelected}
        onSuggestionsFetchRequested={this.handleSuggestionsFetchRequested}
        onSuggestionsClearRequested={this.handleSuggestionsClearRequested}
        renderSuggestionsContainer={this.renderSuggestionsContainer}
        getSuggestionValue={this.getSuggestionText}
        renderSuggestion={this.renderSuggestion}
        shouldRenderSuggestions={this.shouldRenderSuggestions}
        inputProps={{
          blurBehavior: 'add',
          className,
          classes,
          isRequired,
          label,
          meta,
          onChange: this.handleChange,
          resource,
          source,
          value: searchText,
          onFocus: this.handleFocus,
          options,
        }}
      />
    );
  }
}

AutocompleteArrayInput.propTypes = {
  allowEmpty: PropTypes.bool,
  alwaysRenderSuggestions: PropTypes.bool, // used only for unit tests
  choices: PropTypes.arrayOf(PropTypes.object),
  classes: PropTypes.object,
  className: PropTypes.string,
  InputProps: PropTypes.object,
  input: PropTypes.object,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  limitChoicesToValue: PropTypes.bool,
  meta: PropTypes.object,
  options: PropTypes.object,
  optionText: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
  optionValue: PropTypes.string.isRequired,
  resource: PropTypes.string,
  setFilter: PropTypes.func,
  source: PropTypes.string,
  suggestionComponent: PropTypes.func,
  translate: PropTypes.func.isRequired,
  translateChoice: PropTypes.bool.isRequired,
};

AutocompleteArrayInput.defaultProps = {
  choices: [],
  options: {},
  optionText: 'name',
  optionValue: 'id',
  limitChoicesToValue: false,
  translateChoice: true,
};

export default compose(
  addField,
  translate,
  withStyles(styles),
)(AutocompleteArrayInput);



// WEBPACK FOOTER //
// ./src/fields/Autocomplete.jsx